"use client";

import {
  createContext,
  useContext,
  useMemo,
  useState,
  PropsWithChildren,
} from "react";
import {
  type PlayerDatum,
  PositionFilter,
} from "@/components/headToHead/PlayerStats/PlayerStatsTable/util";

const FLEX_POSITIONS = new Set(["RB", "WR", "TE"]);

export const FILTER_FUNCTION_MAPPING: Record<
  PositionFilter,
  (player: PlayerDatum) => boolean
> = {
  [PositionFilter.QUARTERBACK]: (player) => player?.position === "QB",
  [PositionFilter.RUNNING_BACK]: (player) => player?.position === "RB",
  [PositionFilter.RECEIVER]: (player) => player?.position === "WR",
  [PositionFilter.TIGHT_END]: (player) => player?.position === "TE",
  [PositionFilter.FLEX]: (player) => FLEX_POSITIONS.has(player?.position ?? ""),
  [PositionFilter.KICKER]: (player) => player?.position === "K",
  [PositionFilter.DEFENSE]: (player) => player?.position === "D",
};

export type PlayerStatsContextType = {
  position: PositionFilter;
  setPosition: React.Dispatch<React.SetStateAction<PositionFilter>>;
  toPosition: (player: PlayerDatum) => boolean;
  isSelected: (id: PositionFilter) => boolean;
};

const PlayerStatsContext = createContext<PlayerStatsContextType>({
  position: PositionFilter.QUARTERBACK,
  setPosition: () => {},
  toPosition: FILTER_FUNCTION_MAPPING[PositionFilter.QUARTERBACK],
  isSelected: (id: PositionFilter) => id === PositionFilter.QUARTERBACK,
});

export function PlayerStatsContextProvider({ children }: PropsWithChildren) {
  const [position, setPosition] = useState(PositionFilter.QUARTERBACK);
  const toPosition = FILTER_FUNCTION_MAPPING[position];

  const value = useMemo(() => {
    const isSelected = (id: PositionFilter) => id === position;
    return {
      position,
      setPosition,
      toPosition,
      isSelected,
    };
  }, [position, toPosition]);

  return <PlayerStatsContext value={value}>{children}</PlayerStatsContext>;
}

export const usePlayerStatsContext = () => {
  const data = useContext(PlayerStatsContext);
  return data;
};
